import { useRef, useState, useEffect } from 'react';
import { Resizable } from 're-resizable';
import { rotatedBoxDelta } from '../../utils/boxes';
import { gridWidth } from '../../constants/gridConfig';
import { MIN_COLUMN_WIDTH } from '.';
import { useStore } from '../../store/store';
import { effectiveZoom } from '../../utils/zoom';

const selector = ({ updateBox, setEventManagerEnabled, zoomLevel, landscape, setTableColumnWidthsFromCanvas }) => ({
  updateBox,
  setEventManagerEnabled,
  zoomLevel,
  landscape,
  setTableColumnWidthsFromCanvas
})

// Returns the appropriate resize cursor based on how the 'right' DOM handle is visually oriented after rotation
function getResizeCursor(rotation) {
  if (!rotation) return 'ew-resize';
  const normalized = ((rotation % 360) + 360) % 360;
  // When rotated ~90° or ~270°, the DOM 'right' edge is visually at the top or bottom → ns-resize
  if ((normalized >= 45 && normalized < 135) || (normalized >= 225 && normalized < 315)) return 'ns-resize';
  return 'ew-resize';
}

function applyResizeDelta(oldColumnSizeVars, columnKey, nextColKey, deltaX, deltaY, rotation) {
  let colSize = oldColumnSizeVars[`--${columnKey}-size`];
  let nextColSize = oldColumnSizeVars[`--${nextColKey}-size`];
  // Always use 'right' since we're resizing column WIDTH regardless of visual handle direction
  const resizeMove = rotatedBoxDelta({ rotation, direction: 'right', x: deltaX, y: deltaY });
  const nextColTooSmall = nextColSize && resizeMove.width > 0 && nextColSize <= MIN_COLUMN_WIDTH;
  const colTooSmall = resizeMove.width < 0 && colSize <= MIN_COLUMN_WIDTH;

  if (nextColTooSmall || colTooSmall || resizeMove.width === 0) {
    return oldColumnSizeVars;
  }
  const newWidth = Math.max(colSize + resizeMove.width, gridWidth);
  const widthDelta = newWidth - colSize;
  if (nextColSize) {
    if (nextColSize <= MIN_COLUMN_WIDTH && widthDelta > 0) return oldColumnSizeVars;
    if (widthDelta > 0) {
      if (nextColSize <= MIN_COLUMN_WIDTH) return oldColumnSizeVars;
      const allowedDelta = nextColSize - MIN_COLUMN_WIDTH;
      if (allowedDelta >= 0) {
        if (allowedDelta >= widthDelta) {
          nextColSize -= widthDelta;
          colSize += widthDelta;
        } else {
          nextColSize -= allowedDelta;
          colSize += allowedDelta;
        }
      } else return oldColumnSizeVars;
    } else {
      if (colSize <= MIN_COLUMN_WIDTH) return oldColumnSizeVars;
      const allowedDelta = colSize - MIN_COLUMN_WIDTH;
      if (allowedDelta >= 0) {
        if (allowedDelta >= -widthDelta) {
          nextColSize -= widthDelta;
          colSize += widthDelta;
        } else {
          nextColSize -= allowedDelta;
          colSize += allowedDelta;
        }
      } else return oldColumnSizeVars;
    }
    return {
      ...oldColumnSizeVars,
      [`--${nextColKey}-size`]: nextColSize,
      [`--${columnKey}-size`]: colSize,
    };
  }
  // Dernière colonne (pas de nextColKey) : resize libre, change uniquement cette colonne
  const lastColWidth = Math.max(colSize + resizeMove.width, MIN_COLUMN_WIDTH);
  return {
    ...oldColumnSizeVars,
    [`--${columnKey}-size`]: lastColWidth,
  };
}

const ColumnResizer = ({
  rotation,
  setColumnSizeVars,
  columnKey,
  nextColKey,
  boxId,
  resizeEnabled,
  computeMaxColumnWidth,
}) => {
  const resizableRef = useRef(null);
  const headersSizesRef = useRef();
  const pendingDeltaRef = useRef({ x: 0, y: 0 });
  const rafIdRef = useRef(null);
  const [hasClicked, setHasClicked] = useState(false);
  const hasClickedRef = useRef(false);
  const { updateBox, zoomLevel, landscape, setTableColumnWidthsFromCanvas } = useStore(selector);
  const zoomFactorRef = useRef(1);
  const propsRef = useRef({ columnKey, nextColKey, rotation, setColumnSizeVars });
  useEffect(() => {
    propsRef.current = { columnKey, nextColKey, rotation, setColumnSizeVars };
  }, [columnKey, nextColKey, rotation, setColumnSizeVars]);
  zoomFactorRef.current = effectiveZoom(zoomLevel, landscape) / 100;

  const flushResize = useRef(() => {
    const { x, y } = pendingDeltaRef.current;
    if (x === 0 && y === 0) {
      rafIdRef.current = null;
      return;
    }
    pendingDeltaRef.current = { x: 0, y: 0 };
    rafIdRef.current = null;
    const { columnKey: ck, nextColKey: nck, rotation: rot, setColumnSizeVars: setVars } = propsRef.current;
    setVars((oldColumnSizeVars) => {
      const next = applyResizeDelta(oldColumnSizeVars, ck, nck, x, y, rot);
      headersSizesRef.current = next;
      return next;
    });
  }).current;

  const resizeColumn = (...args) => {
    const zoomFactor = zoomFactorRef.current;
    const x = args[0].movementX / zoomFactor;
    const y = args[0].movementY / zoomFactor;
    pendingDeltaRef.current.x += x;
    pendingDeltaRef.current.y += y;
    if (rafIdRef.current == null) {
      rafIdRef.current = requestAnimationFrame(flushResize);
    }
  }

  const handleResizeStop = () => {
    hasClickedRef.current = false;
    setHasClicked(false);
    if (rafIdRef.current != null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    const { x, y } = pendingDeltaRef.current;
    if (x !== 0 || y !== 0) {
      pendingDeltaRef.current = { x: 0, y: 0 };
      const { columnKey: ck, nextColKey: nck, rotation: rot, setColumnSizeVars: setVars } = propsRef.current;
      setVars((old) => {
        const next = applyResizeDelta(old, ck, nck, x, y, rot);
        headersSizesRef.current = next;
        return next;
      });
    }
    if (headersSizesRef.current) {
      updateBox(boxId, (box) => {
        box.content.columns.forEach(({ key }, idx) => {
          const col = box.content.columns[idx];
          if (headersSizesRef.current && headersSizesRef.current[`--${key}-size`]) {
            col.width = headersSizesRef.current[`--${key}-size`];
          }
        });
        box = { ...box };
      });
      headersSizesRef.current = undefined;
      // Tableau variable : sync la largeur (px -> %) dans tableConfig.columns
      // (même valeur que le Drawer). Lit les px fraîchement écrits ci-dessus.
      // No-op pour une table custom.
      setTableColumnWidthsFromCanvas(boxId);
    }
  }

  const handleOnMouseOver = () => {
    document.querySelectorAll('[data-col-cell].column-line-hovered')
      .forEach(el => el.classList.remove('column-line-hovered', 'is-first-column'))
    document.querySelectorAll(`[data-col-cell="${columnKey}"]`)
      .forEach(el => el.classList.add('column-line-hovered'))
  }

  const handleOnMouseLeave = () => {
    if (!hasClicked) {
      document.querySelectorAll('[data-col-cell].column-line-hovered')
        .forEach(el => el.classList.remove('column-line-hovered', 'is-first-column'))
    }
  }

  const handleOnClick = () => {
    setHasClicked(true)
  }

  const handleCursor = getResizeCursor(rotation);

  return (

    <Resizable
      ref={resizableRef}
      enable={{
        top: false,
        right: resizeEnabled,
        bottom: false,
        left: false,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false
      }}
      handleStyles={{
        right: { zIndex: 1000 },
      }}
      minWidth={MIN_COLUMN_WIDTH}
      maxWidth={nextColKey ? computeMaxColumnWidth(columnKey) : undefined}
      style={{ position: 'absolute', top: 0 }}
      size={{ width: "100%", height: "100%" }}
      onResize={resizeColumn}
      onResizeStop={handleResizeStop}
      onClick={handleOnClick}
      onResizeStart={handleOnClick}
      handleComponent={{
        right: <div
          onMouseOver={handleOnMouseOver}
          onMouseLeave={handleOnMouseLeave}
          style={{
            width: "100%",
            height: "100%",
            cursor: handleCursor,
          }} />
      }}
    />
  )
};

export default ColumnResizer;
